
const libPictProvider = require('pict-provider');

// Project cloning is driven entirely server-side via the API's
//   GET  /1.0/ProjectClone/Preview   — returns counts + warnings
//   POST /1.0/ProjectClone/Apply      — builds + pushes the comprehension
//                                       AND writes Configuration via the
//                                       deployed ElevatedUser → Concierge
//                                       impersonation flow
// Earlier the provider required `lib/service/headlight-project-cloning-adapter`
// and ran the adapter in-browser. That approach (a) shipped a big chunk of
// server code into every browser tab, (b) made every entity insert a separate
// browser-→-API round-trip, and (c) had no access to ElevatedUser secrets, so
// Configuration writes hit 403 in environments where the requesting user
// lacked the privilege. Server-side execution closes all three.

// Heavy entities — default EXCLUDED from the initial preview so the first
// build never pays for an Observation / Document fetch the user didn't ask
// for. They still appear in the entity table as opt-in rows (placeholder
// records "—", checkbox unchecked); checking one disables Proceed + offers a
// Refresh preview affordance, and the next build fetches them honoring the
// configured per-entity caps.
const DEFAULT_EXCLUDED_ENTITIES = [ 'Observation', 'Document', 'DocumentObservationJoin' ];

// Core comprehension keys that are NOT user-toggleable in the entity list:
//  - Project is always created/updated (it is the clone destination itself).
//  - Contract has its own dedicated toggle (it depends on the destination
//    project already having a contract), so it is excluded from the generic
//    entity row list.
//  - EmailRuleSchedule is a child of EmailRule: the adapter clones a rule's
//    schedules whenever the rule itself is cloned (a scheduled rule with no
//    schedule row never fires), so the schedule follows the "Email Rule" toggle
//    rather than being independently selectable.
const NON_TOGGLEABLE_ENTITIES = [ 'Project', 'Contract', 'EmailRuleSchedule' ];

// --- Pure helpers (exported for unit testing the workflow without a live
//     rest client / fable). Each is a deterministic transform of plain data. ---

/**
 * Return the configured default-exclusion list (copy, so callers can't mutate
 * the module constant).
 *
 * @returns {Array<string>}
 */
function getDefaultExclusionList()
{
	return DEFAULT_EXCLUDED_ENTITIES.slice();
}

/**
 * Convert raw Project entity rows into selector option rows.
 *
 * @param {Array<object>} pProjects - Project entity records from the rest client.
 * @returns {Array<{ IDProject: number, Name: string, Status: string, IDContract: number }>}
 */
function mapProjectsToOptions(pProjects)
{
	if (!Array.isArray(pProjects))
	{
		return [];
	}
	return pProjects
		.filter((pRow) => pRow && pRow.IDProject)
		.map((pRow) =>
		{
			return {
				IDProject: pRow.IDProject,
				Name: pRow.Name || `Project ${pRow.IDProject}`,
				Status: pRow.Status || '',
				IDContract: pRow.IDContract || 0,
			};
		})
		.sort((a, b) => String(a.Name).localeCompare(String(b.Name)));
}

/**
 * Find an option row by IDProject (string or number id).
 *
 * @param {Array<object>} pOptions
 * @param {string|number} pIDProject
 * @returns {object|null}
 */
function findProjectOption(pOptions, pIDProject)
{
	if (!Array.isArray(pOptions) || pIDProject === '' || pIDProject === null || typeof(pIDProject) === 'undefined')
	{
		return null;
	}
	const tmpID = parseInt(pIDProject, 10);
	if (isNaN(tmpID))
	{
		return null;
	}
	return pOptions.find((pOption) => pOption.IDProject === tmpID) || null;
}

/**
 * The placeholder/suggested name for the destination project, derived from the
 * selected source project. Defaults to "NEW PROJECT" when nothing is selected.
 *
 * @param {object|null} pSourceProject
 * @returns {string}
 */
function computeNameSuggestion(pSourceProject)
{
	if (pSourceProject && pSourceProject.Name)
	{
		return `CLONED FROM ${pSourceProject.Name}`;
	}
	return 'NEW PROJECT';
}

/**
 * Validate that the prepare-step selections are sufficient to generate a
 * comprehension. New-project mode only needs a source; existing-project mode
 * also needs a destination distinct from the source.
 *
 * @param {{ SourceIDProject:(string|number), DestMode:string, DestIDProject:(string|number) }} pState
 * @returns {boolean}
 */
function computeCanGenerate(pState)
{
	if (!pState)
	{
		return false;
	}
	const tmpSource = parseInt(pState.SourceIDProject, 10);
	if (isNaN(tmpSource) || tmpSource <= 0)
	{
		return false;
	}
	if (pState.DestMode === 'existing')
	{
		const tmpDest = parseInt(pState.DestIDProject, 10);
		if (isNaN(tmpDest) || tmpDest <= 0)
		{
			return false;
		}
		if (tmpDest === tmpSource)
		{
			return false;
		}
	}
	return true;
}

/**
 * Turn a camel/PascalCase entity name into a human-friendly label.
 *
 * @param {string} pEntity
 * @returns {string}
 */
function humanizeEntityName(pEntity)
{
	if (!pEntity)
	{
		return '';
	}
	return String(pEntity)
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

/**
 * Read the "records to create" count out of a per-entity count object from the
 * adapter's reportData.Counts. Different slices use different keys
 * (ToCreate / ToUpsert / ToUpdate), so fall back across them.
 *
 * @param {object} pCount
 * @returns {number}
 */
function extractEntityCount(pCount)
{
	if (!pCount || typeof(pCount) !== 'object')
	{
		return 0;
	}
	if (typeof(pCount.ToCreate) === 'number')
	{
		return pCount.ToCreate;
	}
	if (typeof(pCount.ToUpsert) === 'number')
	{
		return pCount.ToUpsert;
	}
	if (typeof(pCount.ToUpdate) === 'number')
	{
		return pCount.ToUpdate;
	}
	// Configuration slice uses { Namespaces, NamespaceValues, ToCopy } — each
	// NamespaceValue is one POST against the Configuration service, so that's
	// the most meaningful "records to apply" number for the entity table.
	if (typeof(pCount.NamespaceValues) === 'number')
	{
		return pCount.NamespaceValues;
	}
	if (typeof(pCount.ToCopy) === 'number')
	{
		return pCount.ToCopy;
	}
	return 0;
}

/**
 * Build the toggleable entity rows for the "Ready for Cloning" step from the
 * adapter's reportData.Counts. Project + Contract are filtered out (Project is
 * mandatory, Contract has its own toggle). The aggregate `Configuration` slice
 * IS included as a row even though it is applied separately from the Meadow
 * comprehension.
 *
 * Also appends opt-in placeholder rows for entities in `pPlaceholderEntities`
 * that the last build excluded entirely (so they're absent from `pCounts`).
 * Placeholders render with `Count: '—'`, an unchecked box, and `NotPreviewed:
 * true` so callers can flag the row visually and so toggling one on can
 * trigger a re-preview.
 *
 * @param {object} pCounts - reportData.Counts.
 * @param {object} pIncludeMap - entityName -> boolean (true = included).
 * @param {Array<string>} [pPlaceholderEntities] - heavy-entity names that
 *   should always appear in the table even when absent from `pCounts`
 *   (typically the default-excluded list).
 * @returns {Array<{ Entity:string, Label:string, Count:(number|string), SourceCount:number, Included:boolean, NotPreviewed:boolean, CheckedAttr:string, RowClass:string }>}
 */
function deriveEntityRows(pCounts, pIncludeMap, pPlaceholderEntities)
{
	const tmpCounts = (pCounts && typeof(pCounts) === 'object') ? pCounts : {};
	const tmpIncludeMap = pIncludeMap || {};
	const tmpPlaceholders = Array.isArray(pPlaceholderEntities) ? pPlaceholderEntities : [];
	const tmpRows = [];
	const tmpSeen = {};
	for (const tmpEntity of Object.keys(tmpCounts))
	{
		if (NON_TOGGLEABLE_ENTITIES.includes(tmpEntity))
		{
			continue;
		}
		const tmpCount = tmpCounts[tmpEntity];
		const tmpIncluded = (tmpIncludeMap[tmpEntity] !== false);
		tmpRows.push({
			Entity: tmpEntity,
			Label: humanizeEntityName(tmpEntity),
			Count: extractEntityCount(tmpCount),
			SourceCount: (tmpCount && typeof(tmpCount.Source) === 'number') ? tmpCount.Source : extractEntityCount(tmpCount),
			Included: tmpIncluded,
			NotPreviewed: false,
			CheckedAttr: tmpIncluded ? 'checked' : '',
			RowClass: tmpIncluded ? '' : 'has-text-grey',
		});
		tmpSeen[tmpEntity] = true;
	}
	// Append placeholder rows for heavy entities the user can opt into but
	// that the last build skipped (so they're absent from Counts).
	for (const tmpEntity of tmpPlaceholders)
	{
		if (tmpSeen[tmpEntity] || NON_TOGGLEABLE_ENTITIES.includes(tmpEntity))
		{
			continue;
		}
		const tmpIncluded = (tmpIncludeMap[tmpEntity] === true);
		tmpRows.push({
			Entity: tmpEntity,
			Label: humanizeEntityName(tmpEntity),
			Count: '—',
			SourceCount: 0,
			Included: tmpIncluded,
			NotPreviewed: true,
			CheckedAttr: tmpIncluded ? 'checked' : '',
			RowClass: 'has-text-grey is-italic',
		});
	}
	return tmpRows;
}

/**
 * Seed the include-map for a freshly generated comprehension: every toggleable
 * entity is ON unless it appears in the default-exclusion list.
 *
 * @param {object} pCounts - reportData.Counts.
 * @returns {object} entityName -> boolean
 */
function buildInitialIncludeMap(pCounts)
{
	const tmpMap = {};
	const tmpCounts = (pCounts && typeof(pCounts) === 'object') ? pCounts : {};
	for (const tmpEntity of Object.keys(tmpCounts))
	{
		if (NON_TOGGLEABLE_ENTITIES.includes(tmpEntity))
		{
			continue;
		}
		tmpMap[tmpEntity] = !DEFAULT_EXCLUDED_ENTITIES.includes(tmpEntity);
	}
	// Always seed an explicit `false` for default-excluded heavy entities,
	// even when they aren't in Counts (the initial build excluded them on
	// purpose). Without this, `toggleEntity` would read their state as
	// undefined and treat the next click as "turn OFF" instead of "turn ON".
	for (const tmpEntity of DEFAULT_EXCLUDED_ENTITIES)
	{
		if (!Object.prototype.hasOwnProperty.call(tmpMap, tmpEntity))
		{
			tmpMap[tmpEntity] = false;
		}
	}
	return tmpMap;
}

/**
 * Produce a pruned copy of a comprehension honoring the user's entity
 * selections. Project is always kept. Contract is kept only when the contract
 * toggle is on. Every other bucket is dropped when its include flag is false.
 *
 * Does not mutate the input comprehension.
 *
 * @param {object} pComprehension
 * @param {object} pIncludeMap - entityName -> boolean.
 * @param {boolean} pIncludeContract
 * @returns {object}
 */
function pruneComprehension(pComprehension, pIncludeMap, pIncludeContract)
{
	const tmpResult = {};
	if (!pComprehension || typeof(pComprehension) !== 'object')
	{
		return tmpResult;
	}
	const tmpIncludeMap = pIncludeMap || {};
	for (const tmpEntity of Object.keys(pComprehension))
	{
		if (tmpEntity === 'Project')
		{
			tmpResult[tmpEntity] = pComprehension[tmpEntity];
			continue;
		}
		if (tmpEntity === 'Contract')
		{
			if (pIncludeContract)
			{
				tmpResult[tmpEntity] = pComprehension[tmpEntity];
			}
			continue;
		}
		// Default-include: only drop when the user explicitly turned it off.
		if (tmpIncludeMap[tmpEntity] !== false)
		{
			tmpResult[tmpEntity] = pComprehension[tmpEntity];
		}
	}
	return tmpResult;
}

class ProjectCloneProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	/** @returns {*} The shared HeadlightRestClient registered on the fable. */
	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	/**
	 * Repaint the view declaratively from current AppData.
	 */
	render()
	{
		this.pict.views.ProjectClone.render();
	}

	// --- State Initialization ---

	initializeState()
	{
		this.pict.AppData.ProjectClone = {
			ActiveStep: 1,
			CompletedSteps: [],

			// Step 1 — Prepare. The source/destination pickers are Select2-backed
			// Project inputs (AJAX via Select2Util.getOptionsAjax), so we only hold
			// the chosen id + display name rather than a pre-fetched project list.
			SourceIDProject: '',
			SourceProjectName: '',
			SourceProjectIDContract: 0,
			DestMode: 'new', // 'new' | 'existing'
			DestIDProject: '',
			DestProjectName: '',
			DestName: '',
			DestDescription: '',
			_nameEdited: false,
			NamePlaceholder: 'NEW PROJECT',
			CanGenerate: false,
			// Single-element-array slots that render the currently-selected <option>
			// inside each Select2 so the selection survives a re-render.
			SourceSelectedSlot: [],
			DestSelectedSlot: [],
			Generating: false,
			GenerateErrorSlot: [],

			// Step 2 — Ready for Cloning
			Build: null,
			EntityRows: [],
			IncludeMap: {},
			IncludeContract: false,
			ContractToggleEnabled: false,
			ContractCount: 0,
			ContractCheckedAttr: '',
			ContractDisabledAttr: 'disabled',
			// Heavy-entity caps. The per-entity Include toggles live in IncludeMap
			// (default OFF for Observation/Document via DEFAULT_EXCLUDED_ENTITIES);
			// these inputs let the user lower the upper bound on the fetch when they
			// DO opt in. Defaults match the adapter recipe defaults.
			MaxRecordsObservation: 5000,
			MaxRecordsDocument: 5000,
			WarningRows: [],
			SummaryLabel: '',

			// Step 3 — Results
			CloneState: {
				Phase: 'idle', // idle | cloning | complete | error
				StatusText: '',
				PanelClass: 'is-hidden',
			},
			ResultSlot: [], // success/error banner (single-element-array trick)
			ResultWarningRows: [],
		};
	}

	// --- Derived render state (called from the view before each render) ---

	deriveRenderState()
	{
		if (!this.pict.AppData.ProjectClone)
		{
			this.initializeState();
		}
		this.deriveStepState();
		this.deriveSelectorState();
		this.deriveEntityState();

		// "Done" is only enabled once the clone has finished (success or failure),
		// so the user can't easily leave mid-clone.
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpPhase = tmpState.CloneState && tmpState.CloneState.Phase;
		tmpState.DoneDisabledAttr = (tmpPhase === 'complete' || tmpPhase === 'error') ? '' : 'disabled';
	}

	/**
	 * Per-step accordion body visibility + status tag, from ActiveStep /
	 * CompletedSteps. Uses Bulma is-hidden.
	 */
	deriveStepState()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		for (let i = 1; i <= 3; i++)
		{
			tmpState['Step' + i + '_BodyClass'] = (tmpState.ActiveStep === i) ? '' : 'is-hidden';
			if (tmpState.CompletedSteps.includes(i))
			{
				tmpState['Step' + i + '_StatusLabel'] = '✓ Complete';
				tmpState['Step' + i + '_StatusClass'] = 'is-success';
			}
			else if (tmpState.ActiveStep === i)
			{
				tmpState['Step' + i + '_StatusLabel'] = 'Active';
				tmpState['Step' + i + '_StatusClass'] = 'is-info';
			}
			else
			{
				tmpState['Step' + i + '_StatusLabel'] = 'Pending';
				tmpState['Step' + i + '_StatusClass'] = '';
			}
		}
	}

	/**
	 * Recompute the dynamic name placeholder, the Select2 selected-option slots,
	 * and the generate-enabled flag.
	 */
	deriveSelectorState()
	{
		const tmpState = this.pict.AppData.ProjectClone;

		tmpState.NamePlaceholder = tmpState.SourceProjectName ?
			`CLONED FROM ${tmpState.SourceProjectName}` :
			'NEW PROJECT';

		// Render the currently-selected <option> back into each Select2 so the
		// selection is preserved across the view's re-renders.
		tmpState.SourceSelectedSlot = tmpState.SourceIDProject ?
			[ { IDProject: tmpState.SourceIDProject, Name: tmpState.SourceProjectName || `Project ${tmpState.SourceIDProject}` } ] :
			[];
		tmpState.DestSelectedSlot = (tmpState.DestMode === 'existing' && tmpState.DestIDProject) ?
			[ { IDProject: tmpState.DestIDProject, Name: tmpState.DestProjectName || `Project ${tmpState.DestIDProject}` } ] :
			[];

		tmpState.DestModeNewChecked = (tmpState.DestMode === 'new') ? 'checked' : '';
		tmpState.DestModeExistingChecked = (tmpState.DestMode === 'existing') ? 'checked' : '';
		tmpState.DestSelectorClass = (tmpState.DestMode === 'existing') ? '' : 'is-hidden';
		// Name + Description only apply when creating a NEW project; for an existing
		// destination they're hidden (the existing project keeps its own name/description).
		tmpState.NewProjectFieldsClass = (tmpState.DestMode === 'new') ? '' : 'is-hidden';

		// The optional contract clone is available whenever the SOURCE project has a
		// contract — the adapter creates a new linked contract or merges into the
		// destination's existing one as appropriate.
		tmpState.ContractToggleEnabled = (tmpState.SourceProjectIDContract > 0);

		tmpState.CanGenerate = computeCanGenerate(tmpState);
		tmpState.GenerateDisabledAttr = (tmpState.CanGenerate && !tmpState.Generating) ? '' : 'disabled';
		tmpState.GenerateButtonClass = tmpState.Generating ? 'is-loading' : '';
	}

	/**
	 * Recompute the entity toggle rows + contract toggle display from the build
	 * result and the include-map.
	 */
	deriveEntityState()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (!tmpState.Build || !tmpState.Build.reportData)
		{
			tmpState.EntityRows = [];
			return;
		}
		const tmpCounts = tmpState.Build.reportData.Counts || {};
		tmpState.EntityRows = deriveEntityRows(tmpCounts, tmpState.IncludeMap, DEFAULT_EXCLUDED_ENTITIES);
		tmpState.ContractCheckedAttr = tmpState.IncludeContract ? 'checked' : '';
		// Disabled when the source has no contract, or while a (re)build is running.
		tmpState.ContractDisabledAttr = (tmpState.ContractToggleEnabled && !tmpState.Generating) ? '' : 'disabled';
		// PendingRebuild fires when the user opts INTO a heavy entity that the
		// last build skipped (it's in IncludeMap as true but absent from Counts).
		// Toggling such a row needs a re-preview to fetch the actual rows / counts.
		// Opting OUT is fine without a rebuild (we just prune at apply time), so
		// it doesn't flip this flag.
		tmpState.PendingRebuild = DEFAULT_EXCLUDED_ENTITIES.some((tmpEntity) =>
			tmpState.IncludeMap && tmpState.IncludeMap[tmpEntity] === true && !Object.prototype.hasOwnProperty.call(tmpCounts, tmpEntity));
		// Proceed + entity toggles are locked while the preview is rebuilding,
		// AND while the user has opted-in heavy entities that haven't been
		// previewed yet (they need to click Refresh first).
		tmpState.ProceedDisabledAttr = (tmpState.Generating || tmpState.PendingRebuild) ? 'disabled' : '';
		tmpState.RebuildingClass = tmpState.Generating ? '' : 'is-hidden';
		tmpState.RefreshHiddenClass = tmpState.PendingRebuild ? '' : 'is-hidden';
		tmpState.RefreshDisabledAttr = tmpState.Generating ? 'disabled' : '';
	}

	// --- Step 1: Prepare for Cloning ---

	/**
	 * Source project chosen in the Select2 picker.
	 *
	 * @param {string} pIDProject - The selected IDProject (empty string when cleared).
	 * @param {string} [pName] - The display name from the Select2 selection.
	 */
	setSource(pIDProject, pName)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		tmpState.SourceIDProject = pIDProject || '';
		tmpState.SourceProjectName = pIDProject ? (pName || '') : '';
		tmpState.SourceProjectIDContract = 0;
		// Auto-fill the suggested destination name until the user edits it.
		if (!tmpState._nameEdited)
		{
			tmpState.DestName = tmpState.SourceProjectName ? `CLONED FROM ${tmpState.SourceProjectName}` : '';
		}
		this.render();
		if (!pIDProject)
		{
			return;
		}
		// Resolve the source's contract: the optional contract clone is available
		// whenever the SOURCE has a contract (it becomes a new linked contract for a
		// new/contract-less destination, or merges into an existing destination's).
		this.pict.providers.Select2Util.getEntityAsync('Project', parseInt(pIDProject, 10))
			.then((pProject) =>
			{
				if (String(tmpState.SourceIDProject) !== String(pIDProject))
				{
					return; // selection changed while the lookup was in flight
				}
				tmpState.SourceProjectIDContract = (pProject && pProject.IDContract) || 0;
				this.render();
			})
			.catch((pError) =>
			{
				this.pict.log.warn(`ProjectClone: could not resolve source project ${pIDProject}: ${pError.message || pError}`);
			});
	}

	setDestMode(pMode)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		tmpState.DestMode = (pMode === 'existing') ? 'existing' : 'new';
		if (tmpState.DestMode === 'new')
		{
			tmpState.DestIDProject = '';
			tmpState.DestProjectName = '';
			tmpState.DestProjectIDContract = 0;
		}
		this.render();
	}

	/**
	 * Destination project chosen in the Select2 picker. The adapter resolves the
	 * destination's own contract state at build time (merge vs. new linked), so we
	 * only need the id + display name here.
	 *
	 * @param {string} pIDProject - The selected IDProject (empty string when cleared).
	 * @param {string} [pName] - The display name from the Select2 selection.
	 */
	setDest(pIDProject, pName)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		tmpState.DestIDProject = pIDProject || '';
		tmpState.DestProjectName = pIDProject ? (pName || '') : '';
		this.render();
	}

	setName(pValue)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		tmpState.DestName = pValue || '';
		tmpState._nameEdited = true;
		// Don't re-render on every keystroke (it would reset the cursor); just
		// recompute the generate-enabled flag is not needed here since name is
		// optional. The value is read back at generate time.
	}

	setDescription(pValue)
	{
		this.pict.AppData.ProjectClone.DestDescription = pValue || '';
	}

	/**
	 * Build the comprehension (dry run) for the current selections and advance to
	 * the "Ready for Cloning" step.
	 */
	generateComprehension()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (!computeCanGenerate(tmpState) || tmpState.Generating)
		{
			return;
		}
		this._runBuild(true);
	}

	/**
	 * Compute the option block sent to /Preview or /Apply from the current
	 * Step 1+2 state. ExcludeEntities is derived from the IncludeMap (entities
	 * the user has unchecked), with DEFAULT_EXCLUDED_ENTITIES used as the seed
	 * before the first preview (so heavy entities don't get fetched on the
	 * initial run).
	 *
	 * @returns {object} option block matching the /Preview + /Apply contract.
	 */
	_buildCloneOptions()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpIncludeMap = tmpState.IncludeMap || {};
		const tmpExclude = (Object.keys(tmpIncludeMap).length === 0)
			? DEFAULT_EXCLUDED_ENTITIES.slice()
			: Object.keys(tmpIncludeMap).filter((tmpEntity) => tmpIncludeMap[tmpEntity] === false);

		// API accepts MaxObservations / MaxDocuments at the top level.
		// Only emit when the user supplied a value; otherwise let the server
		// apply the adapter's recipe defaults.
		const tmpOpts = {
			CloneContract: !!tmpState.IncludeContract,
			ExcludeEntities: tmpExclude,
		};
		const tmpRawObs = parseInt(tmpState.MaxRecordsObservation, 10);
		if (!isNaN(tmpRawObs) && tmpRawObs >= 0) tmpOpts.MaxObservations = tmpRawObs;
		const tmpRawDoc = parseInt(tmpState.MaxRecordsDocument, 10);
		if (!isNaN(tmpRawDoc) && tmpRawDoc >= 0) tmpOpts.MaxDocuments = tmpRawDoc;
		if (tmpState.DestName) tmpOpts.ProjectName = tmpState.DestName;
		if (tmpState.DestDescription) tmpOpts.ProjectDescription = tmpState.DestDescription;
		return tmpOpts;
	}

	/**
	 * Build the GET /ProjectClone/Preview query-string URI from the current
	 * source/dest pickers + option block. URL form so it slots straight into
	 * `restClient.getJSON(uri, cb)`.
	 *
	 * @returns {string} URI suitable for restClient.getJSON
	 */
	_buildPreviewURI()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpSource = parseInt(tmpState.SourceIDProject, 10);
		const tmpIsExisting = (tmpState.DestMode === 'existing');
		const tmpDest = tmpIsExisting ? parseInt(tmpState.DestIDProject, 10) : null;
		const tmpOpts = this._buildCloneOptions();

		const tmpParts = [ `SourceIDProject=${tmpSource}` ];
		if (tmpDest) tmpParts.push(`DestIDProject=${tmpDest}`);
		if (tmpOpts.CloneContract) tmpParts.push('CloneContract=true');
		if (tmpOpts.ExcludeEntities && tmpOpts.ExcludeEntities.length > 0)
		{
			tmpParts.push(`ExcludeEntities=${encodeURIComponent(tmpOpts.ExcludeEntities.join(','))}`);
		}
		if (tmpOpts.MaxObservations !== undefined) tmpParts.push(`MaxObservations=${tmpOpts.MaxObservations}`);
		if (tmpOpts.MaxDocuments !== undefined) tmpParts.push(`MaxDocuments=${tmpOpts.MaxDocuments}`);
		return `ProjectClone/Preview?${tmpParts.join('&')}`;
	}

	/**
	 * Hit GET /ProjectClone/Preview for the current selections + contract
	 * choice. The endpoint builds the comprehension server-side and returns
	 * only the per-entity counts + warnings (the raw comprehension stays on
	 * the server — at production scale it can be 100MB+). Contract toggling
	 * still re-runs preview because CloneContract changes the comprehension
	 * shape (NEW_LINKED vs. UPSERT_INTO_DEST vs. NONE).
	 *
	 * @param {boolean} pAdvanceStep - When true, advance to the Ready step on success.
	 */
	_runBuild(pAdvanceStep)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpSource = parseInt(tmpState.SourceIDProject, 10);
		const tmpIsExisting = (tmpState.DestMode === 'existing');
		const tmpDest = tmpIsExisting ? parseInt(tmpState.DestIDProject, 10) : null;
		const tmpURI = this._buildPreviewURI();

		tmpState.Generating = true;
		tmpState.GenerateErrorSlot = [];
		this.render();

		this.restClient.getJSON(tmpURI, (pError, pBody) =>
		{
			tmpState.Generating = false;
			if (pError)
			{
				tmpState.GenerateErrorSlot = [ { Message: pError.message || String(pError) } ];
				// A failed contract rebuild shouldn't strand the toggle ON with no data.
				tmpState.IncludeContract = false;
				this.render();
				return;
			}

			const tmpBody = pBody || {};
			const tmpReportData = tmpBody.ReportData || {};
			const tmpWarnings = tmpBody.Warnings || [];

			// Stash a lightweight "build summary" so downstream UI bits
			// (entity table, contract count, summary label) keep their
			// existing read shape — no need to refactor every caller.
			tmpState.Build =
				{
					reportData: tmpReportData,
					warnings: tmpWarnings,
				};
			// Preserve the user's entity include/exclude choices across rebuilds.
			const tmpPrevious = tmpState.IncludeMap || {};
			const tmpFresh = buildInitialIncludeMap(tmpReportData.Counts || {});
			for (const tmpKey of Object.keys(tmpFresh))
			{
				if (Object.prototype.hasOwnProperty.call(tmpPrevious, tmpKey))
				{
					tmpFresh[tmpKey] = tmpPrevious[tmpKey];
				}
			}
			tmpState.IncludeMap = tmpFresh;
			tmpState.ContractCount = (tmpReportData.Counts && tmpReportData.Counts.Contract)
				? extractEntityCount(tmpReportData.Counts.Contract)
				: 0;
			tmpState.WarningRows = tmpWarnings.map((pWarning) => ({ Message: pWarning }));
			tmpState.SummaryLabel = this._buildSummaryLabel({ reportData: tmpReportData, SourceIDProject: tmpSource, DestIDProject: tmpDest });

			if (pAdvanceStep)
			{
				this._completeStep(1);
			}
			else
			{
				this.render();
			}
		});
	}

	_buildSummaryLabel(pBuild)
	{
		const tmpReport = pBuild.reportData || {};
		const tmpMode = (tmpReport.ProjectMode === 'AUTO_CREATE') ? 'new project' : `existing project ${tmpReport.DestIDProject}`;
		return `Cloning from project ${tmpReport.SourceIDProject} into a ${tmpMode}.`;
	}

	// --- Step 2: Ready for Cloning ---

	toggleEntity(pEntity)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpCurrent = (tmpState.IncludeMap[pEntity] !== false);
		tmpState.IncludeMap[pEntity] = !tmpCurrent;
		// No rebuild here — opting OUT just prunes at apply time, opting IN
		// for a previously-skipped heavy entity surfaces the PendingRebuild
		// affordance via deriveEntityState (which gates Proceed + reveals
		// the Refresh button). The user then clicks Refresh to actually fetch.
		this.render();
	}

	/**
	 * Re-run the preview with the current IncludeMap so heavy entities the
	 * user just opted into are actually fetched. Invoked by the "Refresh
	 * preview" button surfaced when PendingRebuild is true.
	 */
	refreshPreview()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (tmpState.Generating)
		{
			return;
		}
		this._runBuild(false);
	}

	toggleContract()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (!tmpState.ContractToggleEnabled || tmpState.Generating)
		{
			return;
		}
		tmpState.IncludeContract = !tmpState.IncludeContract;
		// The contract choice changes the comprehension (new linked contract vs.
		// merge vs. none), so rebuild rather than prune.
		this._runBuild(false);
	}

	/**
	 * Update the per-entity record cap for heavy classes (Observation / Document).
	 * Setting a new cap does NOT rebuild — the existing fetch is whatever it is.
	 * The cap applies to the next build (e.g. after toggling the contract on/off,
	 * or after going back to Step 1 and re-generating).
	 *
	 * @param {string} pEntity - 'Observation' or 'Document'.
	 * @param {string|number} pValue - the new cap from the input field.
	 */
	updateMaxRecords(pEntity, pValue)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpParsed = parseInt(pValue, 10);
		if (isNaN(tmpParsed) || tmpParsed < 0) return;
		if (pEntity === 'Observation') tmpState.MaxRecordsObservation = tmpParsed;
		else if (pEntity === 'Document') tmpState.MaxRecordsDocument = tmpParsed;
	}

	/**
	 * Return to the prepare step to adjust selections.
	 */
	backToPrepare()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (tmpState.CloneState.Phase === 'cloning')
		{
			return;
		}
		this.goToStep(1);
	}

	/**
	 * Execute the clone end-to-end via POST /ProjectClone/Apply. The server
	 * builds + pushes the comprehension AND applies the Configuration
	 * namespaces via the concierge impersonation flow (uses the deployed
	 * `Headlight.ElevatedUser`). The browser doesn't need to know about
	 * entity push order, Configuration apply, or pruning — that all
	 * happens server-side, driven by the option block (including
	 * ExcludeEntities derived from the user's checkbox state).
	 */
	proceedWithClone()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (!tmpState.Build)
		{
			return;
		}

		// Move to the results step and lock the UI into the in-progress state.
		this._completeStep(2);
		tmpState.CloneState.Phase = 'cloning';
		tmpState.CloneState.PanelClass = '';
		tmpState.CloneState.StatusText = 'Cloning project — this can take a while for large projects. Please keep this tab open.';
		tmpState.ResultSlot = [];
		tmpState.ResultWarningRows = [];
		this.render();

		const tmpSource = parseInt(tmpState.SourceIDProject, 10);
		const tmpIsExisting = (tmpState.DestMode === 'existing');
		const tmpDest = tmpIsExisting ? parseInt(tmpState.DestIDProject, 10) : null;
		const tmpOpts = this._buildCloneOptions();

		const tmpBody = Object.assign({ SourceIDProject: tmpSource }, tmpOpts);
		if (tmpDest) tmpBody.DestIDProject = tmpDest;

		this.restClient.postJSON('ProjectClone/Apply', tmpBody, (pError, pBody) =>
		{
			if (pError)
			{
				// Full error payload to the console for debugging; UI sees a
				// short, human-friendly description.
				console.error('[ProjectClone] /Apply call failed:', pError);
				tmpState.CloneState.Phase = 'error';
				tmpState.CloneState.StatusText = '';
				tmpState.CloneState.PanelClass = 'is-hidden';
				tmpState.ResultSlot = [ {
					TypeClass: 'is-danger is-soft',
					Icon: 'fa-triangle-exclamation',
					Title: 'Project clone failed.',
					Detail: 'The clone request did not finish successfully. The destination project may be partially populated. Please retry the clone, and if the problem persists, contact your administrator.',
				} ];
				tmpState.ResultWarningRows = (tmpState.Build.warnings || []).map((pWarning) => ({ Message: pWarning }));
				this.render();
				if (this.pict.views.HeadlightPanels)
				{
					this.pict.views.HeadlightPanels.toast({ message: 'Project clone failed — see the result panel for details.', type: 'is-danger' });
				}
				return;
			}

			const tmpResponseReport = (pBody && pBody.ReportData) || {};
			const tmpResponseWarnings = (pBody && pBody.Warnings) || [];
			const tmpResolvedDestID = pBody && pBody.ResolvedDestIDProject;
			// Configuration apply is collected into a per-pair error array
			// rather than aborting the request, so an HTTP 200 can still hide
			// partial failures (e.g. permission-denied on a subset of
			// Configuration NamespaceValues). Treat any Configuration apply
			// errors as a soft-failure: surface them in the result panel so the
			// operator knows the clone is incomplete, and surface a warning
			// toast rather than the green success one.
			const tmpConfigApply = (pBody && pBody.ConfigurationApply) || { applied: 0, errors: [] };
			const tmpConfigErrors = Array.isArray(tmpConfigApply.errors) ? tmpConfigApply.errors : [];
			if (tmpConfigErrors.length > 0)
			{
				// Detailed payload goes to the console for debugging; the UI
				// gets human-readable summaries only.
				console.warn('[ProjectClone] Configuration apply errors (full payload):', tmpConfigErrors);
			}

			// Refresh the local build snapshot from the server's reply so the
			// success detail reflects what was actually executed (e.g. the
			// resolved AUTO_CREATE dest IDProject).
			if (tmpResolvedDestID && tmpResponseReport.ProjectMode === 'AUTO_CREATE')
			{
				tmpResponseReport.DestIDProject = tmpResolvedDestID;
			}
			tmpState.Build = { reportData: tmpResponseReport, warnings: tmpResponseWarnings };

			tmpState.CloneState.Phase = 'complete';
			tmpState.CloneState.StatusText = '';
			tmpState.CloneState.PanelClass = 'is-hidden';
			if (tmpConfigErrors.length === 0)
			{
				tmpState.ResultSlot = [ {
					TypeClass: 'is-success is-soft',
					Icon: 'fa-circle-check',
					Title: 'Project clone was successful!',
					Detail: this._buildSuccessDetail(),
				} ];
			}
			else
			{
				// Partial success: entity push completed, but N Configuration
				// namespace-value writes failed. Keep the panel message
				// user-facing — describe the impact, not the wire-format error.
				tmpState.ResultSlot = [ {
					TypeClass: 'is-warning is-soft',
					Icon: 'fa-triangle-exclamation',
					Title: 'Project clone completed, but some Configuration was not applied.',
					Detail: `${this._buildSuccessDetail()} ${tmpConfigErrors.length} of ${tmpConfigErrors.length + (Number(tmpConfigApply.applied) || 0)} Configuration namespace value(s) could not be written to the destination project. See the list below for which ones.`,
				} ];
			}
			// Merge build warnings + per-namespace Configuration errors into
			// the result panel's warning list. UI surfaces the namespace
			// identifier only; the full error JSON is in the console (logged
			// above) for debugging.
			tmpState.ResultWarningRows = tmpResponseWarnings.map((pWarning) => ({ Message: pWarning }))
				.concat(tmpConfigErrors.map((e) => ({
					Message: `Configuration namespace "${e.NamespaceKey}" value "${e.NamespaceValue}" was not applied to the destination.`
				})));
			this.render();
			if (this.pict.views.HeadlightPanels)
			{
				if (tmpConfigErrors.length === 0)
				{
					this.pict.views.HeadlightPanels.toast({ message: 'Project clone was successful!', type: 'is-success' });
				}
				else
				{
					this.pict.views.HeadlightPanels.toast({ message: 'Project clone finished, but some Configuration was not applied — see the result panel for details.', type: 'is-warning' });
				}
			}
		});
	}

	_buildSuccessDetail()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpReport = (tmpState.Build && tmpState.Build.reportData) || {};
		if (tmpReport.ProjectMode === 'AUTO_CREATE')
		{
			return `A new destination project was created from source project ${tmpReport.SourceIDProject}.`;
		}
		return `Destination project ${tmpReport.DestIDProject} was updated from source project ${tmpReport.SourceIDProject}.`;
	}

	/**
	 * Reset the workflow back to a clean prepare step so re-entry starts fresh.
	 */
	resetWorkflow()
	{
		this.initializeState();
		this.render();
	}

	/**
	 * "Done" handler: only acts once the clone has finished. Resets the workflow
	 * (without rendering, since we're leaving) and navigates back to the Dashboard.
	 */
	finishAndLeave()
	{
		const tmpState = this.pict.AppData.ProjectClone;
		const tmpPhase = tmpState && tmpState.CloneState && tmpState.CloneState.Phase;
		if (tmpPhase !== 'complete' && tmpPhase !== 'error')
		{
			return;
		}
		this.initializeState();
		window.location.hash = '#/Settings';
	}

	// --- Accordion navigation ---

	goToStep(pStepNumber)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (tmpState.CloneState.Phase === 'cloning')
		{
			// No navigation while the clone is in flight.
			return;
		}
		// Only allow jumping to a completed step or the current one.
		if (pStepNumber !== tmpState.ActiveStep && !tmpState.CompletedSteps.includes(pStepNumber) && pStepNumber > tmpState.ActiveStep)
		{
			return;
		}
		tmpState.ActiveStep = pStepNumber;
		this.render();
	}

	_completeStep(pStepNumber)
	{
		const tmpState = this.pict.AppData.ProjectClone;
		if (!tmpState.CompletedSteps.includes(pStepNumber))
		{
			tmpState.CompletedSteps.push(pStepNumber);
		}
		tmpState.ActiveStep = pStepNumber + 1;
		this.render();
	}

}

module.exports = ProjectCloneProvider;

module.exports.default_configuration = {
	ProviderIdentifier: 'ProjectCloneProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
};

// Pure helpers exported for unit tests.
module.exports.getDefaultExclusionList = getDefaultExclusionList;
module.exports.mapProjectsToOptions = mapProjectsToOptions;
module.exports.findProjectOption = findProjectOption;
module.exports.computeNameSuggestion = computeNameSuggestion;
module.exports.computeCanGenerate = computeCanGenerate;
module.exports.humanizeEntityName = humanizeEntityName;
module.exports.extractEntityCount = extractEntityCount;
module.exports.deriveEntityRows = deriveEntityRows;
module.exports.buildInitialIncludeMap = buildInitialIncludeMap;
module.exports.pruneComprehension = pruneComprehension;
module.exports.DEFAULT_EXCLUDED_ENTITIES = DEFAULT_EXCLUDED_ENTITIES;
module.exports.NON_TOGGLEABLE_ENTITIES = NON_TOGGLEABLE_ENTITIES;
